import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { IPlannedRouteDetail, PlannedRouteService, selectMany } from "@tms/_services/planned-route.service";
import { VisitOnTimeStatus } from "../../_enums/VisitOnTimeStatus";
import "./route-performance.component.scss";
import { lastValueFrom, Subscription } from "rxjs";
import { PlannedRouteService2 } from "../../_services/planned-route.service2";
import { IPlannedRouteOptions } from "../../_models/IPlannedRouteOptions";
import { IRoutePerformance } from "../../_models/IRoutePerformance";
import { DateRange } from "@app/enum/DateRange.enum";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { SortType } from "@app/enum/SortType";
import { RoutePerformanceBarComponent } from "../../_components/route-performance-bar/route-performance-bar.component";
import { IAggregateFilterEvent } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";

@Component({
    selector: "route-performance",
    template: require("./route-performance.component.html")
})
export class RoutePerformanceComponent implements OnInit, OnDestroy {

    defaultDateRange = DateRange.Last7Days;
    filterEvent: IAggregateFilterEvent<IPlannedRouteDetail>;

    routes: IPlannedRouteDetail[] = [];

    routePerformance: IRoutePerformance[] = [];
    selectedRow: IRoutePerformance;
    plannedRoutesSubscription: Subscription;
    loading = false;

    tableColumns: IVTableColumn<IRoutePerformance>[];
    tableRows: IVTableRow[];

    selectedRouteTableColumns: IVTableColumn<IPlannedRouteDetail>[];
    selectedRouteTableRows: IVTableRow[];

    constructor(
        private readonly plannedRouteService: PlannedRouteService,
        private readonly plannedRouteService2: PlannedRouteService2,
        private readonly vTableService: VTableService,
        private readonly cd: ChangeDetectorRef) {
    }

    ngOnInit() {
        this.loadData();
    }

    ngOnDestroy(): void {
        this.plannedRoutesSubscription?.unsubscribe();
    }

    rowClick($event: IVTableRow) {
        this.selectedRow = $event.data;
        this.selectedRouteTableRows = this.vTableService.createTableRows(this.selectedRow.routes, this.selectedRouteTableColumns);
        this.cd.detectChanges();
    }

    onRouteRowClicked($event: IVTableRow) {
        this.plannedRouteService.openRouteDetailsDialog($event.data, 'app.tms.routeperformance');
    }

    onFilterChanged(event: IAggregateFilterEvent<IPlannedRouteDetail>) {
        if (!event.trigger) {
            return;
        }

        this.filterEvent = event;

        if (event.trigger === "date") {
            this.requestData(true);
        }
    }

    // ---
    private async requestData(invalidateCache = false): Promise<void> {
        this.loading = true;

        const options: IPlannedRouteOptions = {
            invalidateCache: invalidateCache,
            formatPlannedVisitsForDisplay: true,
            startedRoutesOnly: true
        };

        await lastValueFrom(this.plannedRouteService2.requestPlannedRoutes(this.filterEvent.date[0], this.filterEvent.date[1], options));
    }

    private loadData(): void {
        this.createTable();
        this.createSelectedRowTable();

        this.plannedRoutesSubscription = this.plannedRouteService2.plannedRoutesSubject.subscribe((routes) => {
            this.routes = routes;
            this.processRoutes();
            this.createTableRows();
            this.loading = false;
        });
    }

    private processRoutes() {
        this.routePerformance = [];

        this.routes.forEach((r, index) => {

            let match = this.getOrCreateExistingRoutePerformance(r, index);

            if (!match) {
                return;
            }
            
            let routeVisits = selectMany(match.routes, r => r.PlannedVisits);
            match.arrivalEarlyCount = routeVisits.filter(v => this.plannedRouteService.getArrivalVisitOnTimeStatus(v) == VisitOnTimeStatus.Early).length;
            match.arrivalOnTimeCount = routeVisits.filter(v => this.plannedRouteService.getArrivalVisitOnTimeStatus(v) == VisitOnTimeStatus.OnTime).length;
            match.arrivalLateCount = routeVisits.filter(v => this.plannedRouteService.getArrivalVisitOnTimeStatus(v) == VisitOnTimeStatus.Late).length;
            match.arrivalMissedCount = routeVisits.filter(v => this.plannedRouteService.getArrivalVisitOnTimeStatus(v) == VisitOnTimeStatus.Missed).length;
        });
    }

    private getOrCreateExistingRoutePerformance(r: IPlannedRouteDetail, index: number): IRoutePerformance {

        // Ignore routes with no name
        if (!r.Name) {
            return null;
        }

        let match = this.routePerformance.find(rp => rp.routes.length && this.routesEquivalent(rp.routes[0], r));

        if (!match) {
            match = {
                Id: index,
                name: r.Name,
                arrivalEarlyCount: 0,
                arrivalEarlyMins: 0,
                arrivalLateCount: 0,
                arrivalLateMins: 0,
                arrivalMissedCount: 0,
                arrivalOnTimeCount: 0,
                arrivalOnTimeMins: 0,
                performanceRating: 0,
                routes: []
            };

            this.routePerformance.push(match);
        }

        match.routes.push(r);
        return match;
    }

    private routesEquivalent(r1: IPlannedRouteDetail, r2: IPlannedRouteDetail): boolean {
        if (r1.PlannedVisits.length != r2.PlannedVisits.length) {
            return false;
        }

        for (var i = 0; i < r1.PlannedVisits.length; i++) {
            const r1visit = r1.PlannedVisits[i];
            const r2visit = r2.PlannedVisits[i];

            if (r1visit.Name != r2visit.Name) {
                return false;
            }
        }

        return true;
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "RoutePerformance.RouteName" }),
                createCellFunction: rp => this.vTableService.createTableCell(rp.name)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "RoutePerformance.Routes", sortType: SortType.Number, width: 100, sorted: true }),
                createCellFunction: rp => this.vTableService.createTableCell(rp.routes.length)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "RoutePerformance.Early", sortType: SortType.Number, width: 100 }),
                createCellFunction: rp => this.vTableService.createTableCell(rp.arrivalEarlyCount)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "RoutePerformance.OnTime", sortType: SortType.Number, width: 100 }),
                createCellFunction: rp => this.vTableService.createTableCell(rp.arrivalOnTimeCount)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "RoutePerformance.Late", sortType: SortType.Number, width: 100 }),
                createCellFunction: rp => this.vTableService.createTableCell(rp.arrivalLateCount)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "RoutePerformance.Missed", sortType: SortType.Number, width: 100 }),
                createCellFunction: rp => this.vTableService.createTableCell(rp.arrivalMissedCount)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "RoutePerformance.ArrivalPerformance", sortable: false, excludeFromExport: true }),
                createCellFunction: rp => this.vTableService.createTableCell(rp, {
                    component: RoutePerformanceBarComponent,
                    componentInputs: {
                        routes: rp.routes
                    }
                })
            }
        ]
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.routePerformance, this.tableColumns);
    }

    private createSelectedRowTable(): void {
        this.selectedRouteTableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "RoutePerformance.RouteName" }),
                createCellFunction: route => this.vTableService.createTableCell(route.Name)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "RoutePerformance.StartTime", sorted: true }),
                createCellFunction: route => this.vTableService.createDateCell(route.PlannedStartTime)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "RoutePerformance.ArrivalPerformance", sortable: false, excludeFromExport: true }),
                createCellFunction: route => this.vTableService.createTableCell(route, {
                    component: RoutePerformanceBarComponent,
                    componentInputs: {
                        route: route
                    }
                })
            }
        ]
    }

}
